import {DFGameConfig} from "../DFGameConfig";
import {DumpsterFireSymbol, DumpsterFireSymbolStates, SYMBOL_NAME} from "./DumpsterFireSymbol";
import {ISymbol, ISymbolPosition} from "../../core/reel/simplereel/IReelData";
import {DFSymbolUtils} from "../utils/DFSymbolUtils";
import {gsap, TimelineLite} from "gsap";
import {DumpsterFire} from "../DumpsterFire";
import {DumpsterFireGameMode} from "../server/DumpsterFireGameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {DumpsterFireSound} from "../sound/DumpsterFireSound";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";

export class DumpsterFireReelView extends PIXI.Container {

    private static SYMBOLS_FALL_DURATION: number = 0.25;
    private static SYMBOLS_UNBLUR_DURATION: number = 0.2;
    private static FILL_UP_SYMBOLS_DURATION: number = 0.3;
    private readonly DURATION_SYMBOLS_COLLAPSE_FALL: number = 0.05;
    private readonly DURATION_FAST_SYMBOLS_COLLAPSE_FALL: number = 0.02;
    public static IS_REELS_OUT_PLAYED: boolean = false;
    public static IS_REELS_IN_PLAYED: boolean = false;
    private readonly _reelId: number;
    private _allOutSymbols: ISymbol[] = [];
    private _allInSymbols: ISymbol[] = [];
    private _collapsedReel: ISymbol[] = [];

    constructor(id: number) {
        super();
        this.name = "Reel " + id;
        this._reelId = id;
        this.position = DFGameConfig.reelPositions[id];
    }

    get needCollapse(): boolean {
        return (this._collapsedReel.length != this._allOutSymbols.length);
    }

    public getFillUp(reelId: number): boolean {
        return DumpsterFire.model.reels[reelId].length - this._collapsedReel.length > 0;
    }

    get needFillUp(): boolean {
        return (this._allOutSymbols.length < DFGameConfig.totalReels);
    }

    get isFastSpin(): boolean {
        return DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
    }

    get hasAnticipation(): boolean {
        return !DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
    }

    public getSymbol(position: number): any {
        for (let i: number = 0; i < this.children.length; i++) {
            if ((<DumpsterFireSymbol>this.children[i]).reelPosition === position) {
                return this.children[i];
            }
        }

        // fallbacks
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            if (this._allOutSymbols[i].reelPosition === position) {
                return this._allOutSymbols[i];
            }
        }

        if (position > (this._allOutSymbols.length - 1)) {
            position = (this._allOutSymbols.length - 1);
        }
        return this._allOutSymbols[position];
    }

    public initReel(symbols: string[]): void {
        for (let i: number = 0; i < symbols.length; i++) {
            if (!DumpsterFire.model.isRestore) {
                const symbol: ISymbol = this.createSymbol(symbols[i], DumpsterFireSymbolStates.NORMAL, {
                    reelId: this._reelId,
                    startPosition: (i),
                    targetPosition: (i)
                });
                this._allOutSymbols.push(symbol);
            } else if (DumpsterFire.model.isRestore && DumpsterFire.model.data.nextMode.indexOf("AVALANCHE") > -1) {
                if (symbols[i] !== "AV") {
                    const symbol: ISymbol = this.createSymbol(symbols[i], DumpsterFireSymbolStates.NORMAL, {
                        reelId: this._reelId,
                        startPosition: (i),
                        targetPosition: (i)
                    });
                    this._collapsedReel.push(symbol);
                }
            } else if (DumpsterFire.model.isRestore && (DumpsterFire.model.data.nextMode === DumpsterFireGameMode.EXTRA_SPIN)) {
                if (symbols[i] !== "AV") {
                    const symbol: ISymbol = this.createSymbol(symbols[i], DumpsterFireSymbolStates.NORMAL, {
                        reelId: this._reelId,
                        startPosition: (i),
                        targetPosition: (i)
                    });
                    this._allOutSymbols.push(symbol);
                }
            } else {
                const symbol: ISymbol = this.createSymbol(symbols[i], DumpsterFireSymbolStates.NORMAL, {
                    reelId: this._reelId,
                    startPosition: (i),
                    targetPosition: (i)
                });
                this._allOutSymbols.push(symbol);
            }
        }
    }

    public createSymbol(symbolName: string, state: DumpsterFireSymbolStates, symbolPositionData: ISymbolPosition): ISymbol {
        if (symbolName === SYMBOL_NAME.X_BOMB && DumpsterFire.model.findUpgradedSymbol("XB") && DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1) {
            symbolName = SYMBOL_NAME.U_X_BOMB;
        }
        if (symbolName === SYMBOL_NAME.SCATTER && DumpsterFire.model.findUpgradedSymbol("S") && DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1) {
            symbolName = SYMBOL_NAME.U_SCATTER;
        }
        if (symbolName === SYMBOL_NAME.SCATTER && !(DumpsterFire.model.data.mode.indexOf("NORMAL") > -1 || DumpsterFire.model.data.nextMode.indexOf("NORMAL") > -1)) {
            symbolName = SYMBOL_NAME.FS_SCATTER;
        }
        const symbol: DumpsterFireSymbol = DFSymbolUtils.createSymbol(symbolName, state, symbolPositionData);
        symbol.position.set(0, symbolPositionData.startPosition * DFGameConfig.symbolHeight);
        symbol.reelTargetPosition = symbolPositionData.targetPosition;
        symbol.reelPosition = symbolPositionData.startPosition;
        symbol.scale.set(0.95);
        this.addChild(symbol);
        return symbol;
    }

    public collapseReel(reelDelay: number): gsap.core.Timeline {
        this._collapsedReel = [];
        for (let i: number = this._allOutSymbols.length - 1; i > -1; i--) {
            if (this._allOutSymbols[i].isRemoved) {
                // If i = 0 it's the top symbol which can't fall down
                if (i > 0) {
                    for (let j: number = i; j > 0; j--) {
                        if (!this._allOutSymbols[j - 1].isRemoved) {
                            this._allOutSymbols[j - 1].reelTargetPosition++;
                        }
                    }
                }
            } else {
                this._collapsedReel.push(this._allOutSymbols[i]);
            }
        }
        return this.getCollapseAnimation(reelDelay);
    }

    public getAllOutAnimation(): TimelineLite {
        const returnTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let counter: number = 0;
        if (!DumpsterFireReelView.IS_REELS_OUT_PLAYED) {
            returnTimeline.add(() => {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.REELS_OUT);
            });
            DumpsterFireReelView.IS_REELS_OUT_PLAYED = true;
            DumpsterFireReelView.IS_REELS_IN_PLAYED = false;
        }

        while (this._allOutSymbols.length > 0) {
            const symbol: DumpsterFireSymbol = <DumpsterFireSymbol>this._allOutSymbols.pop()!;
            symbol.reelTargetPosition = DFGameConfig.totalSymsPerReel;
            if (symbol == null) {
                throw new Error(`AvalancheReelPartAnimator.animateAllOut, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                timeline.add([
                    gsap.timeline({delay: DumpsterFireReelView.SYMBOLS_UNBLUR_DURATION}).add((<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.NORMAL, symbol.symbolName)),
                    gsap.to(symbol.position, {
                        duration: DumpsterFireReelView.SYMBOLS_FALL_DURATION,
                        y: DFGameConfig.symbolHeight * (symbol.reelTargetPosition) + 100,
                        ease: "power2.in",
                        delay: DFGameConfig.reelSpinStartSymbolDelay * counter
                    })
                ]);
                timeline.add(() => this.removeAvalancheSymbol(symbol));
                subTimelines.push(timeline);
            }
            counter++;
        }
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public removeAvalancheSymbol(symbol: ISymbol) {
        this.removeChild(<DumpsterFireSymbol>symbol);
    }

    public stop(reelData: string[], reel: number, nearWinReels: boolean, currentWinReel: boolean, fastSpin: boolean): TimelineLite {
        const tl: gsap.core.Timeline = gsap.timeline();
        DumpsterFire.allReelsView.needFillUpReels = [true, true, true, true, true, true];
        for (let i: number = 0; i < DFGameConfig.totalSymsPerReel; i++) {
            const symbol: ISymbol = this.createSymbol(reelData[i], DumpsterFireSymbolStates.NORMAL, {
                reelId: this._reelId,
                startPosition: -((DFGameConfig.totalSymsPerReel - i) + 1),
                targetPosition: i
            });
            this._allInSymbols.push(symbol);
        }
        tl.add(this.getAllInAnimation(reel, nearWinReels, currentWinReel, fastSpin));

        return tl;
    }

    public clear() {
        while (this._allInSymbols.length > 0) {
            const sym = this._allInSymbols.pop();
            if (sym) {
                this.removeAvalancheSymbol(sym);
            }
        }
        while (this._allOutSymbols.length > 0) {
            const sym = this._allOutSymbols.pop();
            if (sym) {
                this.removeAvalancheSymbol(sym);
            }
        }
        while (this._collapsedReel.length > 0) {
            const sym = this._collapsedReel.pop();
            if (sym) {
                this.removeAvalancheSymbol(sym);
            }
        }
    }

    public getAllInAnimation(reel: number, nearWinReels: boolean, currentWinReel: boolean, fastSpin: boolean): gsap.core.Timeline {
        const returnTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let counter: number = 0;
        this._allOutSymbols = [];
        let nearWinDelay: number = 0;
        let isFirstSymbol: boolean = true;
        while (this._allInSymbols.length > 0) {
            const symbol: ISymbol = this._allInSymbols.pop()!;
            this._allOutSymbols.unshift(symbol);
            if (symbol == null) {
                throw new Error(`AvalancheReelPartAnimator.animateAllIn, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                //#421 stop spin fast if feature buy
                const fastSpin: boolean = DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false),
                    totalSpinDuration: number = fastSpin ? DFGameConfig.mainAllInTotalSpinDurationFastSpin : DFGameConfig.mainAllInTotalSpinDuration,
                    symSpinStopDuration: number = (fastSpin ? DFGameConfig.mainReelSpinStopSymbolDelayFastSpin : DFGameConfig.mainReelSpinStopSymbolDelay) + (counter * 0.02);
                timeline.add([
                    gsap.timeline({delay: fastSpin ? DFGameConfig.mainReelAllInBlurDelayFastSpin + (counter * 0.03) : (DFGameConfig.mainReelAllInBlurDelay + (counter * 0.01))}).add((<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.NORMAL, symbol.name)),
                    gsap.to(symbol.position, {
                        duration: totalSpinDuration,
                        y: DFGameConfig.symbolHeight * symbol.reelTargetPosition,
                        ease: "power3.in",
                        delay: symSpinStopDuration + nearWinDelay
                    })]);
                timeline.add(() => {
                    symbol.reelPosition = symbol.reelTargetPosition
                });
                if (isFirstSymbol && DumpsterFire.model.scatterCount < 6) {
                    timeline.add(this.firstSymbolLandingOnReel(reel), (symSpinStopDuration + nearWinDelay + totalSpinDuration));
                }
                if (isFirstSymbol) {
                    if (!DumpsterFireReelView.IS_REELS_IN_PLAYED) {
                        timeline.add(() => {
                            if (fastSpin) {
                                DumpsterFire.soundPlayer.playSound(DumpsterFire.soundPlayer.playReelsIn(DumpsterFireSound.FAST_REELS_IN_ARRAY));
                            } else {
                                DumpsterFire.soundPlayer.playSound(DumpsterFire.soundPlayer.playReelsIn(DumpsterFireSound.NORMAL_REELS_IN_ARRAY));
                            }
                        }, symSpinStopDuration + nearWinDelay + totalSpinDuration);
                        DumpsterFireReelView.IS_REELS_IN_PLAYED = true;
                        DumpsterFireReelView.IS_REELS_OUT_PLAYED = false;
                    }
                    if (currentWinReel && isFirstSymbol && !fastSpin) {
                        if (!((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0)) {
                            timeline.add(() => {
                                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.REELS_LANDING_ANTICIPATION);
                            }, (symSpinStopDuration + nearWinDelay + totalSpinDuration))
                        }
                    }
                    isFirstSymbol = false;
                }
                timeline.add(() => {
                    this.symbolBounce(<DumpsterFireSymbol>symbol)
                }, (symSpinStopDuration + nearWinDelay + totalSpinDuration))
                timeline.add(() => {
                    this.symbolLanding(this._reelId, symbol.reelTargetPosition, currentWinReel, symbol);
                }, symSpinStopDuration + nearWinDelay + totalSpinDuration);
                timeline.add(() => {
                    if (nearWinReels && DumpsterFire.model.scatterCount < 5 && DumpsterFire.model.scatterCount > 1 && !fastSpin) {
                        DumpsterFire.model.scatterSymArray.forEach((sym) => {
                            timeline.add(DumpsterFire.winView.playGlowAnimation(<DumpsterFireSymbol>sym))
                        }, symSpinStopDuration + nearWinDelay + totalSpinDuration)
                        timeline.add(() => {
                            DumpsterFire.reelAnticipation.update(reel + 1)
                        });
                    }
                }, symSpinStopDuration + nearWinDelay + totalSpinDuration);
                timeline.add(() => {
                    if (DumpsterFire.model.scatterCount > 4 && !DumpsterFire.model.upgradeStop) {
                        DumpsterFire.model.scatterSymArray.forEach((sym) => {
                            timeline.add(DumpsterFire.winView.playGlow_2_Animation(<DumpsterFireSymbol>sym))
                        });
                        timeline.add(DumpsterFire.reelAnticipation.reset(false));
                        DumpsterFire.model.upgradeStop = true;
                    }
                }, symSpinStopDuration + nearWinDelay + totalSpinDuration + 0.1)
                subTimelines.push(timeline);
            }
            counter++;
        }
        returnTimeline.add(subTimelines, 0);
        if (DumpsterFire.model.scatterCount === 5) {
            DumpsterFire.model.scatterCount++;
        }
        returnTimeline.to(this, {duration: 0.02});
        return returnTimeline;
    }

    private symbolLanding(reelId: number, rowId: number, nearWinReels: boolean, symbol: ISymbol): void {
        const activeBetFeature: BetFeature | undefined = DumpsterFire.apiPlugin.betFeatureController.getActiveBetFeature();
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.X_BOMB) {
            DumpsterFire.soundPlayer.playSound(DumpsterFire.soundPlayer.playReelsIn(DumpsterFireSound.X_BOMB_ARRAY));
        }
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.SCATTER) {
            if (!(DumpsterFire.model.data.mode.indexOf("AVALANCHE") > -1)) {
                DumpsterFire.model.scatterCount++;
                DumpsterFire.model.scatterSymArray.push(symbol);
            }
            if (DumpsterFire.model.scatterCount > 2 && !DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.S_LANDING_3_UP);
            } else if (DumpsterFire.model.scatterCount === 2) {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.S_LANDING_1_2);
            } else if (DumpsterFire.model.scatterCount === 1 && activeBetFeature?.name !== "BOOSTED_BET") {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.S_LANDING_1_2);
            }
        }
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.X_WAYS) {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.X_WAYS_LANDING);
        }
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.XI) {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.XI_LANDING);
        }
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.WILD) {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.WILD_LANDING);
        }
        if (DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.FS_SCATTER || DumpsterFire.model.data.reels[reelId][rowId] === SYMBOL_NAME.U_SCATTER) {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.S_LANDING_1_2);
        }
    }

    private firstSymbolLandingOnReel(reel: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (DumpsterFire.model.nearWinReels[reel]) {
            if (!DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
                tl.add(DFSymbolUtils.shake(1, 1, 500, 8, 0.1), 0);
            }
        }
        return tl;
    }

    public getFillUpReelAnimation(delay: number, reel: number, nearWinReel: boolean, fastSpin: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        let reelLength: number = DumpsterFire.model.reels[this._reelId].length;
        const numberOfFillUpSymbols: number = reelLength - this._collapsedReel.length;
        const innerTl: gsap.core.Timeline = gsap.timeline();
        let isFirstSymbol: boolean = true;
        let updateReel: number;
        for (let i: number = reel + 1; i < 6; i++) {
            if (DumpsterFire.model.symbolCollapsed[i]) {
                updateReel = i;
                break;
            }
        }
        for (let i: number = numberOfFillUpSymbols - 1; i >= 0; i--) {
            const symbol: ISymbol = this.createSymbol(DumpsterFire.model.gameData.reels[this._reelId][i], DumpsterFireSymbolStates.NORMAL, {
                reelId: this._reelId,
                startPosition: (0 - ((numberOfFillUpSymbols - i))),
                targetPosition: i
            });
            const toSymbol = DumpsterFire.model.data.reels[this._reelId][symbol.reelTargetPosition]
            symbol.reelPosition = -2;
            this._collapsedReel.unshift(symbol);
            innerTl.add(DumpsterFire.winView.playLandingAnimation(<DumpsterFireSymbol>symbol, toSymbol), 0);
            if (isFirstSymbol && DumpsterFire.model.scatterCount < 5) {
                innerTl.add([() => {
                    isFirstSymbol = false;
                },
                    this.firstSymbolLandingOnReel(this._reelId)
                ], 0);
            }
            if (DumpsterFire.model.data.reels[this._reelId][symbol.reelTargetPosition] === SYMBOL_NAME.SCATTER) {
                DumpsterFire.model.scatterCount++;
                DumpsterFire.model.scatterSymArray.push(symbol);
            }
            if (DumpsterFire.model.scatterCount < 5 && DumpsterFire.model.scatterCount > 1 && !fastSpin && numberOfFillUpSymbols > 0 && nearWinReel) {
                DumpsterFire.model.scatterSymArray.forEach((sym) => {
                    innerTl.add(DumpsterFire.winView.playGlowAnimation(<DumpsterFireSymbol>sym))
                }, 0)
                innerTl.add(() => {
                    if (DumpsterFire.model.symbolCollapsed[updateReel]) {
                        DumpsterFire.reelAnticipation.update(updateReel);
                    }
                }, 0);
            }
            if (DumpsterFire.model.scatterCount > 4 && !DumpsterFire.model.upgradeAvalancheStop) {
                DumpsterFire.model.scatterSymArray.forEach((sym) => {
                    innerTl.add(DumpsterFire.winView.playGlow_2_Animation(<DumpsterFireSymbol>sym), 0)
                });
                innerTl.add(DumpsterFire.reelAnticipation.reset(false), 0);
                DumpsterFire.model.upgradeAvalancheStop = true;
            }
            innerTl.add(() => {
                this.symbolLanding(this._reelId, symbol.reelTargetPosition, nearWinReel, symbol);
            }, 0);
        }
        tl.add(this.getFillUpAnimation(delay, nearWinReel, fastSpin), 0);
        tl.add(innerTl);
        return tl;
    }

    private getCollapseAnimation(reelDelay: number): gsap.core.Timeline {
        const fastSpin: boolean = DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
        const collapseTimeline: gsap.core.Timeline = gsap.timeline({delay: fastSpin ? 0 : reelDelay});
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        this._allOutSymbols.reverse();
        let isFirstSymbol: boolean = true;
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            const symbol: DumpsterFireSymbol = <DumpsterFireSymbol>this._allOutSymbols[i];
            if (symbol == null) {
                throw new Error(`AvalancheReelPartAnimator.collapseAnimation, symbol is null!`);
            } else {
                if (symbol.reelTargetPosition > symbol.reelPosition) {
                    timeline = gsap.timeline();
                    const duration: number = (symbol.reelTargetPosition === symbol.position.y) ? 0 : ((fastSpin ? this.DURATION_FAST_SYMBOLS_COLLAPSE_FALL : this.DURATION_SYMBOLS_COLLAPSE_FALL) * (i + 1));
                    if (duration > 0) {
                        timeline.add([
                            gsap.to(symbol.position, {
                                duration: duration,
                                y: DFGameConfig.symbolHeight * (symbol.reelTargetPosition),
                                ease: "power2.in",
                            })
                        ]);
                    }
                    if (DumpsterFire.model.firstReelCollapse && isFirstSymbol) {
                        timeline.add(() => {
                            DumpsterFire.soundPlayer.playSound(DumpsterFire.soundPlayer.playReelsIn(DumpsterFireSound.COLLAPSE_REEL_SOUND_ARRAY))
                        });
                        isFirstSymbol = false;
                        DumpsterFire.model.firstReelCollapse = false;
                    }
                    subTimelines.push(timeline);
                }
            }
        }
        // this._allOutSymbols.reverse();
        collapseTimeline.add(subTimelines, 0.3);
        return collapseTimeline;
    }

    private getFillUpAnimation(reelDelay: number, nearWinReels: boolean, fastSpin: boolean): gsap.core.Timeline {
        const fillUpTimeline = gsap.timeline({delay: reelDelay});
        let timeline: gsap.core.Timeline;
        let counter: number = 0;
        const subTimelines: gsap.core.Timeline[] = [];
        this._allOutSymbols = [];
        let isFirstSym: boolean = true;
        while (this._collapsedReel.length > 0) {
            const symbol: ISymbol = this._collapsedReel.pop()!;
            this._allOutSymbols.push(symbol);
            if (symbol == null) {
                throw new Error(`AvalancheReelPartAnimator.animateFillUp, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                if (symbol.reelTargetPosition > symbol.reelPosition) {
                    timeline.add([
                        gsap.timeline({delay: fastSpin ? DFGameConfig.mainReelAllInBlurDelayFastSpin + (counter * 0.03) : (DFGameConfig.mainReelAllInBlurDelay + (counter * 0.01))}),
                        gsap.to(symbol.position, {
                            duration: DumpsterFireReelView.FILL_UP_SYMBOLS_DURATION,
                            y: DFGameConfig.symbolHeight * symbol.reelTargetPosition,
                            ease: "power3.in",
                            delay: 0.02 * counter
                        })]);
                    timeline.add(() => {
                        symbol.reelPosition = symbol.reelTargetPosition;
                    });
                    if (isFirstSym) {
                        if (nearWinReels && !fastSpin) {
                            if (!((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0)) {
                                timeline.add(() => {
                                    DumpsterFire.soundPlayer.playSound(DumpsterFireSound.REELS_LANDING_ANTICIPATION);
                                })
                            }
                        }
                        if (DumpsterFire.model.firstReelFillUp) {
                            timeline.add(() => {
                                DumpsterFire.soundPlayer.playSound(DumpsterFire.soundPlayer.playReelsIn(DumpsterFireSound.FIIL_UP_REEL_SOUND_ARRAY))
                            });
                        }
                        isFirstSym = false;
                        DumpsterFire.model.firstReelFillUp = false;
                        if (DumpsterFire.model.scatterCount === 5) {
                            DumpsterFire.model.scatterSymArray.forEach((sym) => {
                                timeline.add(DumpsterFire.winView.playGlow_2_Animation(<DumpsterFireSymbol>sym))
                            });
                            timeline.add(DumpsterFire.reelAnticipation.reset(false));
                            DumpsterFire.model.scatterCount++;
                        }
                    }
                    subTimelines.push(timeline);
                }
            }
            counter++;
        }
        fillUpTimeline.add(subTimelines, 0);
        fillUpTimeline.add(() => {
            this._allOutSymbols = this.sortSymbolArray(this._allOutSymbols);
        });
        return fillUpTimeline;
    }

    private symbolBounce(symbol: DumpsterFireSymbol, newSymbol: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (newSymbol) {
            if (DumpsterFire.model.data.nextMode === DumpsterFireGameMode.NORMAL || DumpsterFire.model.data.nextMode === DumpsterFireGameMode.FREESPIN) {
                tl.add(symbol.getStateAnimation(DumpsterFireSymbolStates.LANDING, symbol.symbolName)).timeScale(1);
            } else {
                tl.add(symbol.getStateAnimation(DumpsterFireSymbolStates.LANDING, symbol.symbolName));
            }
        }
        return tl;
    }

    private sortSymbolArray(toBeSorted: ISymbol[]): ISymbol[] {
        const sortedSymbols: ISymbol[] = [];
        //Sorting symbols with reel position 1-X (position 0 is top padding position)
        for (let i: number = 0; i < DumpsterFire.model.evaluatedReels[this._reelId].length; i++) {
            for (let j: number = 0; j < toBeSorted.length; j++) {
                if (toBeSorted[j].reelPosition == i) {
                    sortedSymbols.push(toBeSorted[j]);
                    break;
                }
            }
        }
        return sortedSymbols;
    }
}